import { useEffect, useState } from "react";
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query";
import { CloseIcon, Drawer, GripIcon, PlusIcon, TrashIcon, useConfirm, useToast } from "@signet/ui";
import { api, useSortable } from "@signet/web-kit";
import type { DefaultsInput, Tracker, TrackerDefaults } from "../shared/onboarding-tracker.ts";
import { BASE, KEY, StepIcon } from "./onboarding-tracker.tsx";

/* ──────────────────────────────────────────────────────────────────────────
   "Edit defaults" on the onboarding tracker (Jay, 2026-11-04): add, rename,
   delete and drag the steps, and the lines under each one. The steps are the
   table's columns for everyone; the lines are what a creator starts with
   when they join, so creators already on the tracker keep their own. Nothing
   is saved until Save, so a half-finished edit never shows on Ben's Now.
   ────────────────────────────────────────────────────────────────────────── */

type DraftLine = { id: string; label: string };
type DraftStep = { id: string; label: string; icon: string | null; lines: DraftLine[] };

// Built on use: this file and onboarding-tracker.tsx import each other, so KEY is not ready while this one loads.
const defaultsKey = () => [...KEY, "defaults"] as const;
let counter = 0;
const newId = () => `new-${Date.now().toString(36)}-${(++counter).toString(36)}`;

/** The handle is a plain span: a press on a button or a field never starts a drag. */
function Handle({ label }: { label: string }) {
  return (
    <span className="cr-ot-def-grip" aria-hidden="true" title={`Drag ${label}`}>
      <GripIcon size={13} />
    </span>
  );
}

export function DefaultsEditor({ onClose }: { onClose: () => void }) {
  const client = useQueryClient();
  const toast = useToast();
  const { confirm, element } = useConfirm();
  const defaults = useQuery({ queryKey: defaultsKey(), queryFn: () => api<TrackerDefaults>(`${BASE}/defaults`) });
  const [draft, setDraft] = useState<DraftStep[] | null>(null);

  useEffect(() => {
    if (defaults.data && !draft) setDraft(defaults.data.steps.map((s) => ({ id: s.id, label: s.label, icon: s.icon, lines: s.lines.map((l) => ({ ...l })) })));
  }, [defaults.data, draft]);

  const save = useMutation({
    mutationFn: (body: DefaultsInput) => api<{ defaults: TrackerDefaults; tracker: Tracker }>(`${BASE}/defaults`, { method: "PUT", body }),
    onSuccess: (data) => {
      client.setQueryData(defaultsKey(), data.defaults);
      client.setQueryData(KEY, data.tracker);
      toast.success("Defaults saved");
      onClose();
    },
    onError: (error) => toast.error("Couldn't save", error instanceof Error ? error.message : undefined),
  });

  const steps = draft ?? [];
  const setStep = (id: string, change: (s: DraftStep) => DraftStep) => setDraft((d) => d && d.map((s) => (s.id === id ? change(s) : s)));

  const sortSteps = useSortable({
    ids: steps.map((s) => s.id),
    groupOf: () => "steps",
    prefix: "cr-ot",
    // A press inside a step's own lines belongs to the lines' drag, not the step's.
    ignore: "button, a, input, select, textarea, .cr-ot-def-lines",
    onMove: (_id, _place, order) => setDraft((d) => d && order.map((id) => d.find((s) => s.id === id)!)),
  });

  const blank = steps.some((s) => !s.label.trim() || s.lines.some((l) => !l.label.trim()));

  const submit = async () => {
    const before = defaults.data?.steps ?? [];
    const gone = before.filter((s) => !steps.some((d) => d.id === s.id));
    if (gone.length) {
      const ok = await confirm({
        title: gone.length === 1 ? `Remove ${gone[0]!.label}?` : `Remove ${gone.length} steps?`,
        body: `${gone.map((s) => s.label).join(", ")} ${gone.length === 1 ? "comes" : "come"} off the tracker for every creator. Their ticks and lines are kept, not deleted.`,
        confirmLabel: "Remove and save",
        danger: true,
      });
      if (!ok) return;
    }
    save.mutate({ steps: steps.map((s) => ({ id: s.id, label: s.label.trim(), lines: s.lines.map((l) => ({ id: l.id, label: l.label.trim() })) })) });
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title="Edit defaults"
      subtitle="The steps every creator has, and the lines a new creator starts with. Creators already on the tracker keep their own lines."
      footer={
        <>
          <button type="button" className="secondary-button compact" onClick={onClose}>
            Cancel
          </button>
          <span className="spacer" />
          <button type="button" className="button brass compact" disabled={!draft || blank || !steps.length || save.isPending} onClick={() => void submit()}>
            {save.isPending ? "Saving" : "Save"}
          </button>
        </>
      }
    >
      {element}
      {defaults.isError ? <p className="faint">Couldn't load the defaults.</p> : null}
      {draft ? (
        <div className="cr-ot-def">
          <ul className="cr-ot-def-steps">
            {steps.map((s) => (
              <li key={s.id} className="cr-ot-def-step" {...sortSteps.rowProps(s.id)}>
                <div className="cr-ot-def-stephead">
                  <Handle label={s.label || "this step"} />
                  <span className="cr-ot-def-icon" aria-hidden="true">
                    <StepIcon icon={s.icon} size={14} />
                  </span>
                  <input
                    className="cr-ot-def-steplabel"
                    value={s.label}
                    maxLength={60}
                    placeholder="Step name"
                    aria-label="Step name"
                    onChange={(e) => setStep(s.id, (st) => ({ ...st, label: e.target.value }))}
                  />
                  <button
                    type="button"
                    className="icon-button ghost small"
                    aria-label={`Delete ${s.label || "this step"}`}
                    onClick={() => setDraft((d) => d && d.filter((x) => x.id !== s.id))}
                  >
                    <TrashIcon size={13} />
                  </button>
                </div>
                <StepLines step={s} onChange={(lines) => setStep(s.id, (st) => ({ ...st, lines }))} />
              </li>
            ))}
          </ul>
          <button
            type="button"
            className="text-button tiny accent cr-ot-def-addstep"
            onClick={() => setDraft((d) => d && [...d, { id: newId(), label: "", icon: null, lines: [] }])}
          >
            <PlusIcon size={13} /> Add a step
          </button>
          {blank ? <p className="faint cr-ot-def-hint">Give every step and line a name, or delete it, before saving.</p> : null}
        </div>
      ) : defaults.isLoading ? (
        <p className="faint">Loading</p>
      ) : null}
    </Drawer>
  );
}

function StepLines({ step, onChange }: { step: DraftStep; onChange: (lines: DraftLine[]) => void }) {
  const sort = useSortable({
    ids: step.lines.map((l) => l.id),
    groupOf: () => step.id,
    prefix: "cr-ot",
    onMove: (_id, _place, order) => onChange(order.map((id) => step.lines.find((l) => l.id === id)!)),
  });
  return (
    <ul className="cr-ot-def-lines">
      {step.lines.map((l) => (
        <li key={l.id} className="cr-ot-def-line" {...sort.rowProps(l.id)}>
          <Handle label={l.label || "this line"} />
          <input
            value={l.label}
            maxLength={200}
            placeholder="Line"
            aria-label="Line"
            onChange={(e) => onChange(step.lines.map((x) => (x.id === l.id ? { ...x, label: e.target.value } : x)))}
          />
          <button
            type="button"
            className="icon-button ghost small"
            aria-label={`Delete "${l.label || "this line"}"`}
            onClick={() => onChange(step.lines.filter((x) => x.id !== l.id))}
          >
            <CloseIcon size={12} />
          </button>
        </li>
      ))}
      <li className="cr-ot-def-addline">
        <button type="button" className="text-button tiny" onClick={() => onChange([...step.lines, { id: newId(), label: "" }])}>
          <PlusIcon size={12} /> Add a line
        </button>
      </li>
    </ul>
  );
}
